/**
 * Custom Fields Service
 * 
 * Handles clinic-specific custom CMR fields including:
 * - Field definition management
 * - CMR data pre-population mapping
 * - Cross-clinic data sharing
 * - Patient custom field values
 */

import { db } from './firebase';
import firebase from 'firebase/compat/app';

// ============================================================================
// CMR FIELD MAPPING CONFIGURATION
// ============================================================================

/**
 * Maps common custom field names to CMR data paths
 * Used to pre-populate custom fields from existing CMR data
 */
const CMR_FIELD_MAPPINGS = {
  // Insurance fields
  'primary insurance': { path: 'insurance.primary', type: 'string' },
  'insurance provider': { path: 'insurance.primary', type: 'string' },
  'insurance company': { path: 'insurance.primary', type: 'string' },
  'insurance': { path: 'insurance.primary', type: 'string' },
  
  // Pharmacy fields
  'preferred pharmacy': { path: 'pharmacy.name', type: 'string' },
  'pharmacy name': { path: 'pharmacy.name', type: 'string' },
  'pharmacy': { path: 'pharmacy.name', type: 'string' },
  'pharmacy address': { path: 'pharmacy.address', type: 'string' },
  
  // Language/Communication
  'preferred language': { path: 'demographics.language', type: 'string' },
  'language preference': { path: 'demographics.language', type: 'string' },
  'language': { path: 'demographics.language', type: 'string' },
  
  // Emergency Contact
  'emergency contact name': { path: 'demographics.emergencyContact.name', type: 'string' },
  'emergency contact': { path: 'demographics.emergencyContact.name', type: 'string' },
  'emergency contact phone': { path: 'demographics.emergencyContact.phone', type: 'string' },
  'emergency phone': { path: 'demographics.emergencyContact.phone', type: 'string' },
  
  // Address
  'home address': { path: 'demographics.address', type: 'string' },
  'mailing address': { path: 'demographics.address', type: 'string' },
  'address': { path: 'demographics.address', type: 'string' },
};

/**
 * Standard field keys for cross-clinic matching
 * Maps normalized field names to standard keys
 */
const STANDARD_FIELD_KEYS = {
  'primary_insurance': ['primary insurance', 'insurance provider', 'insurance company', 'insurance'],
  'preferred_pharmacy': ['preferred pharmacy', 'pharmacy name', 'pharmacy'],
  'preferred_language': ['preferred language', 'language preference', 'language'],
  'emergency_contact_name': ['emergency contact name', 'emergency contact'],
  'emergency_contact_phone': ['emergency contact phone', 'emergency phone'],
  'transportation_needs': ['transportation assistance', 'transportation needs', 'transportation'],
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Normalize field name for matching (case-insensitive, remove special chars)
 * @param {string} fieldName - Original field name
 * @returns {string} Normalized field name
 */
export function normalizeFieldName(fieldName) {
  if (!fieldName) return '';
  return fieldName.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
}

/**
 * Generate field key from field name for cross-clinic matching
 * @param {string} fieldName - Original field name
 * @param {string} standardFieldKey - Optional standard field key from supervisor mapping
 * @returns {string} Field key (normalized identifier)
 */
export function generateFieldKey(fieldName, standardFieldKey = null) {
  // If supervisor mapped to standard key, use that
  if (standardFieldKey) {
    return standardFieldKey;
  }
  
  const normalized = normalizeFieldName(fieldName);
  
  // Check if matches standard key variations
  for (const [key, variations] of Object.entries(STANDARD_FIELD_KEYS)) {
    if (variations.some(v => normalized.includes(v) || v.includes(normalized))) {
      return key;
    }
  }
  
  // Generate from field name (e.g., "Primary Insurance Provider" → "primary_insurance_provider")
  return normalized.replace(/\s+/g, '_');
}

/**
 * Get CMR value for a custom field if it maps to CMR data
 * @param {string} fieldName - Custom field name
 * @param {Object} cmrData - Patient CMR data
 * @returns {Object|null} { found: boolean, value: any, source: 'cmr' } or null
 */
export function mapFieldToCMRData(fieldName, cmrData) {
  if (!fieldName || !cmrData) return null;
  
  const normalized = normalizeFieldName(fieldName);
  const mapping = CMR_FIELD_MAPPINGS[normalized];
  
  if (!mapping) return null;
  
  // Navigate CMR data path (e.g., 'demographics.emergencyContact.name')
  const pathParts = mapping.path.split('.');
  let value = cmrData;
  
  for (const part of pathParts) {
    if (value && typeof value === 'object' && part in value) {
      value = value[part];
    } else {
      return null;
    }
  }
  
  if (value == null || value === '') return null;
  
  return {
    found: true,
    value: value,
    source: 'cmr'
  };
}

// ============================================================================
// CLINIC CUSTOM FIELDS MANAGEMENT
// ============================================================================

/**
 * Get all custom fields for a clinic
 * @param {string} clinicId - Clinic ID
 * @returns {Promise<Array>} Array of custom field definitions
 */
export async function getClinicCustomFields(clinicId) {
  if (!clinicId) {
    console.warn('[CustomFields] No clinicId provided');
    return [];
  }
  
  try {
    const snapshot = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('customFields')
      .orderBy('order', 'asc')
      .get();
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('[CustomFields] Error fetching clinic custom fields:', error);
    return [];
  }
}

/**
 * Get active custom fields for a clinic
 * @param {string} clinicId - Clinic ID
 * @returns {Promise<Array>} Array of active custom field definitions
 */
export async function getActiveClinicCustomFields(clinicId) {
  const allFields = await getClinicCustomFields(clinicId);
  return allFields.filter(field => field.active !== false);
}

/**
 * Create a new custom field
 * @param {string} clinicId - Clinic ID
 * @param {Object} fieldData - Field definition
 * @param {string} createdBy - User ID of supervisor creating the field
 * @returns {Promise<string>} Field ID
 */
export async function createCustomField(clinicId, fieldData, createdBy) {
  if (!clinicId || !fieldData || !createdBy) {
    throw new Error('Missing required parameters');
  }
  
  // Get current max order to append new field
  const existingFields = await getClinicCustomFields(clinicId);
  const maxOrder = existingFields.length > 0 
    ? Math.max(...existingFields.map(f => f.order || 0))
    : -1;
  
  const fieldDoc = {
    fieldName: fieldData.fieldName,
    fieldType: fieldData.fieldType,
    fieldOptions: fieldData.fieldOptions || null,
    required: fieldData.required || false,
    order: maxOrder + 1,
    active: true,
    standardFieldKey: fieldData.standardFieldKey || null, // Optional mapping to standard key
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    createdBy: createdBy
  };
  
  const docRef = await db
    .collection('clinics')
    .doc(clinicId)
    .collection('customFields')
    .add(fieldDoc);
  
  return docRef.id;
}

/**
 * Update a custom field
 * @param {string} clinicId - Clinic ID
 * @param {string} fieldId - Field ID
 * @param {Object} updates - Field updates
 * @returns {Promise<void>}
 */
export async function updateCustomField(clinicId, fieldId, updates) {
  if (!clinicId || !fieldId) {
    throw new Error('Missing required parameters');
  }
  
  await db
    .collection('clinics')
    .doc(clinicId)
    .collection('customFields')
    .doc(fieldId)
    .update({
      ...updates,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp()
    });
  
}

/**
 * Delete a custom field (soft delete - preserves data)
 * @param {string} clinicId - Clinic ID
 * @param {string} fieldId - Field ID
 * @returns {Promise<void>}
 */
export async function deleteCustomField(clinicId, fieldId) {
  // Soft delete - set active to false, preserve data
  await updateCustomField(clinicId, fieldId, { active: false });
}

/**
 * Reorder custom fields
 * @param {string} clinicId - Clinic ID
 * @param {Array<string>} fieldIds - Array of field IDs in new order
 * @returns {Promise<void>}
 */
export async function reorderFields(clinicId, fieldIds) {
  if (!clinicId || !Array.isArray(fieldIds)) {
    throw new Error('Invalid parameters');
  }
  
  const batch = db.batch();
  
  fieldIds.forEach((fieldId, index) => {
    const fieldRef = db
      .collection('clinics')
      .doc(clinicId)
      .collection('customFields')
      .doc(fieldId);
    
    batch.update(fieldRef, { order: index });
  });
  
  await batch.commit();
}

// ============================================================================
// PATIENT CUSTOM FIELD VALUES
// ============================================================================

/**
 * Get patient's custom field values for a specific clinic
 * @param {string} clinicId - Clinic ID
 * @param {string} patientId - Patient ID
 * @returns {Promise<Object>} Custom field values { [fieldId]: { value, updatedAt, updatedBy } }
 */
export async function getPatientCustomFieldsForClinic(clinicId, patientId) {
  if (!clinicId || !patientId) {
    return {};
  }
  
  try {
    const doc = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('patientCustomData')
      .doc(patientId)
      .get();
    
    if (!doc.exists) {
      return {};
    }
    
    return doc.data().customFields || {};
  } catch (error) {
    console.error('[CustomFields] Error fetching patient custom fields:', error);
    return {};
  }
}

/**
 * Get patient's shared custom field value (cross-clinic)
 * @param {string} patientId - Patient ID
 * @param {string} fieldKey - Standard field key
 * @returns {Promise<Object|null>} Shared field value or null
 */
export async function getSharedCustomField(patientId, fieldKey) {
  if (!patientId || !fieldKey) {
    return null;
  }
  
  try {
    const doc = await db
      .collection('patients')
      .doc(patientId)
      .collection('sharedCustomFields')
      .doc(fieldKey)
      .get();
    
    if (!doc.exists) {
      return null;
    }
    
    return doc.data();
  } catch (error) {
    console.error('[CustomFields] Error fetching shared custom field:', error);
    return null;
  }
}

/**
 * Save patient's custom field values for a clinic
 * @param {string} clinicId - Clinic ID
 * @param {string} patientId - Patient ID
 * @param {Object} fieldValues - Field values { [fieldId]: value }
 * @param {string} updatedBy - User ID (patient or provider)
 * @returns {Promise<void>}
 */
export async function savePatientCustomFields(clinicId, patientId, fieldValues, updatedBy) {
  if (!clinicId || !patientId || !fieldValues) {
    throw new Error('Missing required parameters');
  }
  
  // Get field definitions to check for standard field keys
  const fieldDefinitions = await getClinicCustomFields(clinicId);
  const fieldMap = new Map(fieldDefinitions.map(f => [f.id, f]));
  
  // Prepare custom fields data
  const customFields = {};
  const sharedFieldsToSave = {};
  
  for (const [fieldId, value] of Object.entries(fieldValues)) {
    const fieldDef = fieldMap.get(fieldId);
    if (!fieldDef) continue;
    
    // Save to clinic-specific location
    customFields[fieldId] = {
      value: value,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedBy: updatedBy
    };
    
    // Also save to shared location if field has standard field key
    const fieldKey = generateFieldKey(fieldDef.fieldName, fieldDef.standardFieldKey);
    if (STANDARD_FIELD_KEYS[fieldKey] || fieldDef.standardFieldKey) {
      sharedFieldsToSave[fieldKey] = {
        value: value,
        fieldName: fieldDef.fieldName,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedBy: updatedBy
      };
    }
  }
  
  // Save clinic-specific data
  const patientDataRef = db
    .collection('clinics')
    .doc(clinicId)
    .collection('patientCustomData')
    .doc(patientId);
  
  await patientDataRef.set({
    patientId: patientId,
    customFields: customFields,
    lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
    lastVisitAt: firebase.firestore.FieldValue.serverTimestamp()
  }, { merge: true });
  
  // Save shared fields (cross-clinic)
  const batch = db.batch();
  for (const [fieldKey, fieldData] of Object.entries(sharedFieldsToSave)) {
    const sharedRef = db
      .collection('patients')
      .doc(patientId)
      .collection('sharedCustomFields')
      .doc(fieldKey);
    
    // Get existing to update sourceClinics array
    const existing = await sharedRef.get();
    const existingData = existing.exists ? existing.data() : {};
    const sourceClinics = existingData.sourceClinics || [];
    
    if (!sourceClinics.includes(clinicId)) {
      sourceClinics.push(clinicId);
    }
    
    batch.set(sharedRef, {
      ...fieldData,
      sourceClinics: sourceClinics
    }, { merge: true });
  }
  
  if (Object.keys(sharedFieldsToSave).length > 0) {
    await batch.commit();
  }
  
}

// ============================================================================
// PRELOAD LOGIC (Multi-Source Priority)
// ============================================================================

/**
 * Preload custom field value from all available sources
 * Priority: CMR → Previous visit at clinic → Cross-clinic shared → Empty
 * @param {Object} field - Field definition
 * @param {string} clinicId - Clinic ID
 * @param {string} patientId - Patient ID
 * @param {Object} cmrData - Patient CMR data
 * @returns {Promise<Object>} { value: any, source: 'cmr' | 'previous_visit' | 'cross_clinic' | null }
 */
export async function preloadCustomFieldValue(field, clinicId, patientId, cmrData) {
  // Priority 1: Check CMR data
  const cmrMapping = mapFieldToCMRData(field.fieldName, cmrData);
  if (cmrMapping && cmrMapping.found) {
    return {
      value: cmrMapping.value,
      source: 'cmr'
    };
  }
  
  // Priority 2: Check previous visit at same clinic
  const clinicData = await getPatientCustomFieldsForClinic(clinicId, patientId);
  if (clinicData[field.id] && clinicData[field.id].value != null) {
    return {
      value: clinicData[field.id].value,
      source: 'previous_visit'
    };
  }
  
  // Priority 3: Check cross-clinic shared data
  const fieldKey = generateFieldKey(field.fieldName, field.standardFieldKey);
  const sharedData = await getSharedCustomField(patientId, fieldKey);
  if (sharedData && sharedData.value != null) {
    return {
      value: sharedData.value,
      source: 'cross_clinic'
    };
  }
  
  // Priority 4: Empty
  return {
    value: null,
    source: null
  };
}

/**
 * Preload all custom fields for a patient at a clinic
 * @param {Array<Object>} fields - Array of field definitions
 * @param {string} clinicId - Clinic ID
 * @param {string} patientId - Patient ID
 * @param {Object} cmrData - Patient CMR data
 * @returns {Promise<Object>} Preloaded values { [fieldId]: { value, source } }
 */
export async function preloadAllCustomFields(fields, clinicId, patientId, cmrData) {
  const preloaded = {};
  
  for (const field of fields) {
    const preload = await preloadCustomFieldValue(field, clinicId, patientId, cmrData);
    preloaded[field.id] = preload;
  }
  
  return preloaded;
}

export default {
  // Field management
  getClinicCustomFields,
  getActiveClinicCustomFields,
  createCustomField,
  updateCustomField,
  deleteCustomField,
  reorderFields,
  
  // Patient data
  getPatientCustomFieldsForClinic,
  getSharedCustomField,
  savePatientCustomFields,
  
  // Preload
  preloadCustomFieldValue,
  preloadAllCustomFields,
  
  // Utilities
  normalizeFieldName,
  generateFieldKey,
  mapFieldToCMRData
};
